import { useTranslation } from 'react-i18next'

import { PrimaryButton } from '@/components/ui-kit/Buttons'
import { FXOverlay } from '@/components/ui-kit/FXOverlay'
import { MascotBubble, MascotSlot } from '@/components/ui-kit/Mascot'
import { AnimatedCounter, CompareBar, StatCard } from '@/components/ui-kit/Primitives'
import { ScreenShell, TopBar } from '@/components/ui-kit/Shell'
import { de } from '@/lib/format'
import { estimateDeviation, estimateVerdict, totalTax } from '@/lib/game'
import { cn } from '@/lib/utils'
import type { Balance, PhoneMatch } from '@/types/game'

interface BalanceScreenProps {
    balance: Balance
    matches: PhoneMatch[]
    guess: number | null
    onContinue?: () => void
}

/**
 * Die persönliche Bilanz - der Moment, auf den die Stunde hinausläuft.
 *
 * Die Abweichung zur eigenen Schätzung wird farblich unterschieden:
 * Überschätzen ist der übliche Fall und wird rot markiert, Unterschätzen grün.
 * Die Richtung ist die eigentliche Lernerfahrung, nicht die Zahl.
 */
export function BalanceScreen({ balance, matches, guess, onContinue }: BalanceScreenProps) {
    const { t } = useTranslation()

    const won = balance.totalDelta > 0
    const even = balance.totalDelta === 0
    // WETTBANDIT-68: Wer gar nicht gewettet hat, steht bei genau 0 EUR - dort
    // sah ein "+0,00 EUR" nach einem winzigen Gewinn aus. Ohne Wette gibt es
    // kein Vorzeichen, und der Waschbaer lobt das ausdruecklich.
    const noBets = balance.placed === 0
    const verdict = guess === null ? null : estimateVerdict(guess, balance.totalDelta)
    const deviation = guess === null ? 0 : estimateDeviation(guess, balance.totalDelta)
    const tax = totalTax(matches)

    return (
        <ScreenShell>
            <FXOverlay kind={won ? 'confetti' : even ? 'none' : 'rain'} />
            <TopBar title={t('balance.title')} />

            {/* Innenabstand fuer den Ring und den Schatten der Sprechblase: overflow-y-auto
                schneidet alles ab, was ueber die Box hinausragt - seit die Blase bis an
                den Rand reicht (WETTBANDIT-44), traf es rechts den Ring und unten den
                Schatten. -mx-1 haelt die Inhalte trotzdem auf ihrer Linie. */}
            <div className="relative z-1 -mx-1 wb-scroll flex flex-1 flex-col gap-4 overflow-y-auto px-1 pt-4 pb-8">
                <div className="text-center">
                    <p className="text-[11px] font-bold tracking-[.12em] text-wb-dim uppercase">
                        {noBets
                            ? t('balance.heroNoBets')
                            : won
                              ? t('balance.heroWin')
                              : even
                                ? t('balance.heroEven')
                                : t('balance.heroLoss')}
                    </p>
                    <div
                        className={cn(
                            'mt-1 wb-display text-5xl font-extrabold',
                            won ? 'text-wb-win' : even ? 'text-wb-text' : 'text-wb-loss',
                        )}
                        data-testid="hero-balance"
                    >
                        <AnimatedCounter value={balance.totalDelta} signed={!even} />
                    </div>
                    <p className="mt-1 text-xs text-wb-muted">{t('balance.startedWith')}</p>

                    {noBets && (
                        <p className="mt-2 text-sm text-wb-text" data-testid="hero-no-bets-note">
                            {t('balance.noBetsNote')}
                        </p>
                    )}

                    {/* Ein Gewinn liest sich sonst wie eine Leistung. Genau der
                        Trugschluss haelt Menschen am Automaten - deshalb steht
                        hier, woran es lag und was auf Dauer passiert. */}
                    {won && (
                        <p
                            data-testid="hero-win-note"
                            className="mx-auto mt-2.5 max-w-[280px] text-[12px] leading-snug text-wb-warn"
                        >
                            {t('balance.heroWinNote')}
                        </p>
                    )}
                </div>

                <div className="flex gap-2">
                    <StatCard
                        label={t('balance.stats.correct')}
                        value={`${balance.correct}/${balance.placed}`}
                        tone="win"
                    />
                    <StatCard
                        label={t('balance.stats.stakes')}
                        value={`${balance.stakes} €`}
                        tone="loss"
                    />
                    <StatCard
                        label={t('balance.stats.payout')}
                        value={`${de(balance.totalPayout)} €`}
                        tone="accent"
                    />
                </div>

                {/* Die Steuer faellt nur auf Treffer an und verschwindet sonst
                    lautlos in der Auszahlung. Sie ist aber der Grund, warum
                    auch eine faire Quote auf Dauer verliert - deshalb steht sie
                    hier als eigene Zahl. */}
                {tax > 0 && (
                    <p
                        data-testid="balance-tax"
                        className="text-center text-[11px] leading-snug text-wb-muted"
                    >
                        {t('balance.taxNote', { total: de(tax) })}
                    </p>
                )}

                {guess !== null && verdict !== null && (
                    <section className="flex flex-col gap-3 rounded-2xl border border-wb-border bg-wb-panel p-4">
                        <h3 className="text-sm font-extrabold">{t('balance.compare.title')}</h3>

                        <CompareBar
                            label={t('balance.compare.guess')}
                            value={guess}
                            max={Math.max(Math.abs(guess), Math.abs(balance.totalDelta), 1)}
                            tone="warn"
                        />
                        <CompareBar
                            label={t('balance.compare.actual')}
                            value={balance.totalDelta}
                            max={Math.max(Math.abs(guess), Math.abs(balance.totalDelta), 1)}
                            tone={balance.totalDelta >= 0 ? 'win' : 'loss'}
                        />

                        <p
                            data-testid="deviation"
                            data-verdict={verdict}
                            className={cn(
                                'rounded-xl px-3 py-2.5 text-sm font-bold',
                                verdict === 'over' && 'bg-wb-loss/10 text-wb-loss',
                                verdict === 'under' && 'bg-wb-win/10 text-wb-win',
                                verdict === 'close' && 'bg-wb-panel-hi text-wb-dim',
                            )}
                        >
                            {verdict === 'close'
                                ? t('balance.compare.close')
                                : t(`balance.compare.${verdict}`, {
                                      amount: `${de(Math.abs(deviation))} €`,
                                  })}
                        </p>
                    </section>
                )}

                <section className="flex flex-col gap-2">
                    <h3 className="text-[10px] font-bold tracking-[.08em] text-wb-dim uppercase">
                        {t('balance.matches')}
                    </h3>

                    <ul className="flex flex-col gap-1.5">
                        {matches.map((match) => {
                            const skipped = match.pick === null || match.pick === 'skip'
                            const hit = !skipped && match.pick === match.result

                            return (
                                <li
                                    key={match.position}
                                    className="flex items-center gap-2 rounded-xl border border-wb-border bg-wb-panel px-3 py-2 text-[13px]"
                                >
                                    <span className="w-5 wb-num text-wb-muted">
                                        {match.position}
                                    </span>
                                    <span className="min-w-0 flex-1 truncate">
                                        {match.home.short} – {match.away.short}
                                    </span>
                                    <span className="wb-num text-wb-dim">
                                        {match.score
                                            ? `${match.score.home}:${match.score.away}`
                                            : '–'}
                                    </span>
                                    <span
                                        className={cn(
                                            'w-20 text-right wb-num font-bold',
                                            skipped && 'text-wb-muted',
                                            !skipped && hit && 'text-wb-win',
                                            !skipped && !hit && 'text-wb-loss',
                                        )}
                                    >
                                        {skipped
                                            ? t('balance.matchSkipped')
                                            : hit
                                              ? `+${de(
                                                    Math.round(
                                                        (match.odds[
                                                            match.pick as 'home' | 'draw' | 'away'
                                                        ] *
                                                            10 *
                                                            0.95 -
                                                            10) *
                                                            100,
                                                    ) / 100,
                                                )} €`
                                              : '−10,00 €'}
                                    </span>
                                </li>
                            )
                        })}
                    </ul>
                </section>

                <div className="flex items-center gap-2.5">
                    <MascotSlot pose={noBets ? 'greeting' : won ? 'money' : 'laughing'} size={88} />
                    <MascotBubble>
                        {noBets
                            ? t('balance.bubbleNoBets')
                            : won
                              ? t('balance.bubbleWin')
                              : t('balance.bubbleLoss')}
                    </MascotBubble>
                </div>
            </div>

            {onContinue && (
                <PrimaryButton className="relative z-1 mt-3" onClick={onContinue}>
                    {t('common.next')}
                </PrimaryButton>
            )}
        </ScreenShell>
    )
}
